import QRCode from "qrcode";
import hmacSHA256 from "crypto-js/hmac-sha256";
import "share-api-polyfill";

// TYPES

const donateeGroups = <const>["PlantTrees", "CleanOcean", "ProtectWildlife"];
type DonateeGroup = typeof donateeGroups[number];
type Language = "de" | "en";

// CONFIG

const IS_DEV = process.env.APP_ENV !== "production";
const BASE_URL = `https://${IS_DEV ? "dev" : "www"}.thanku.social`;
const LANGUAGE: Language = "de";
const MIN_BUDGET = 100;
const PRICING: { [K in DonateeGroup]: number } = {
  // business pricing 01/2024
  CleanOcean: 2.5, // per kg in €
  PlantTrees: 2.5, // per tree in €
  ProtectWildlife: 0.25, // per sqm in €
};

// STATE

type Impact = { type: DonateeGroup; value: number };

type BatchEntry =
  | { status: "Ok"; link: string }
  | { status: "Err"; error: string };

type State = {
  page: PageId | null;
  slug: string | null;
  token: string | null;
  orderId: string | null;
  message: string | null;
  thanku: { url: URL; donateeGroup: DonateeGroup; impactValue: number } | null;
  totalPrice: number;
  isDev: boolean;
  impactType: DonateeGroup;
  impactInputValue: string;
  batchSizeInputValue: string;
  batchStatus: "notAsked" | "processing" | "done";
  batchResult: BatchEntry[];
  budget: number;
  allow1sqm: boolean;
  hideFreeImpactSelection: boolean;
  buttonConfig: Impact[];
};

type StateKey = keyof State;

const initialState: State = {
  page: null,
  slug: null,
  token: null,
  orderId: null,
  message: null,
  thanku: null,
  totalPrice: 0,
  isDev: IS_DEV,
  impactType: "ProtectWildlife",
  impactInputValue: "10",
  batchSizeInputValue: "1",
  batchStatus: "notAsked",
  batchResult: [],
  budget: MIN_BUDGET,
  allow1sqm: false,
  hideFreeImpactSelection: false,
  buttonConfig: [],
};

// DOM

const dom = {
  page: {
    init: $("#page-init"),
    select: $("#page-select"),
    loading: $("#page-loading"),
    qrcode: $("#page-qrcode"),
    batch: $("#page-batch"),
  },
  init: {
    form: $("#init-form") as HTMLFormElement,
    inputSlug: $("#init-slug") as HTMLInputElement,
    inputToken: $("#init-token") as HTMLInputElement,
    inputOrderId: $("#init-order-id") as HTMLInputElement,
    inputMessage: $("#init-message") as HTMLTextAreaElement,
    inputBudget: $("#init-budget") as HTMLInputElement,
    buttonConfig: $("#init-buttonConfig") as HTMLTextAreaElement,
    checkboxFreeImpactSelection: $(
      "#init-freeImpactSelection"
    ) as HTMLInputElement,
  },
  select: {
    impactQuickSelection: $("#impact-quick-selection") as HTMLDivElement,
    impactButtonTemplate: $("#impact-button-template") as HTMLTemplateElement,
    impactForm: $("#impact-form") as HTMLFormElement,
    impactValue: $("#impact-value") as HTMLInputElement,
    decImpactValue: $("#dec-impact-value") as HTMLButtonElement,
    incImpactValue: $("#inc-impact-value") as HTMLButtonElement,
    impactType: $("#impact-type") as HTMLSelectElement,
    batchSize: $("#batch-size") as HTMLInputElement,
    decBatchSize: $("#dec-batch-size") as HTMLButtonElement,
    incBatchSize: $("#inc-batch-size") as HTMLButtonElement,
    submitImpact: $("#submit-impact") as HTMLButtonElement,
    costsImpact: $("#costs-impact") as HTMLSpanElement,
    btnBack: $("#back") as HTMLButtonElement,
  },
  qrcode: {
    canvas: $("#qrcode-canvas") as HTMLCanvasElement,
    url: $("#qrcode-url") as HTMLAnchorElement,
    btnNext: $("#qrcode-btn-next") as HTMLButtonElement,
    impact: $("#qrcode-impact") as HTMLElement,
    btnShare: $("#btn-share") as HTMLButtonElement,
  },
  batch: {
    countCurrent: $("#batch-count-current") as HTMLElement,
    countTotal: $("#batch-count-total") as HTMLElement,
    countOk: $("#batch-count-ok") as HTMLElement,
    countErr: $("#batch-count-err") as HTMLElement,
    linkList: $("#batch-link-list") as HTMLTextAreaElement,
    downloadLink: $("#batch-download-link") as HTMLAnchorElement,
    btnBack: $("#batch-btn-back") as HTMLButtonElement,
    loading: $("#batch-loading") as HTMLElement,
  },
  counter: $("#counter") as HTMLElement,
  slug: $("#slug") as HTMLButtonElement,
  dev: $("#dev") as HTMLElement,
};

type PageId = keyof typeof dom.page;

const pageIds = Object.keys(dom.page) as PageId[];

// MAIN

document.addEventListener("DOMContentLoaded", run);

// COMMAND

type None = { cmdType: "None" };
type Alert = { cmdType: "Alert"; message: string };
type Confirm = { cmdType: "Confirm"; message: string; onConfirm: () => Msg };
type CreateThankU = {
  cmdType: "CreateThankU";
  donateeGroup: DonateeGroup;
  impactValue: number;
  onResult: (result: URL | Error) => Msg;
};
type CreateThankUBatch = {
  cmdType: "CreateThankUBatch";
  donateeGroup: DonateeGroup;
  impactValue: number;
  batchSize: number;
  onEntryResult: (result: URL | Error) => Msg;
  onBatchDone: () => Msg;
};
type StoreTotalPrice = { cmdType: "StoreTotalPrice"; totalPrice: number };
type DisposeStorage = { cmdType: "DisposeStorage" };
type TestConfig = {
  cmdType: "TestConfig";
  slug: string;
  token: string;
  orderId: string;
  message: string;
  onResult: (result: null | Error) => Msg;
};
type ConfigToURL = { cmdType: "ConfigToURL" };
type ShareLink = { cmdType: "ShareLink"; url: string };

type Cmd =
  | None
  | Alert
  | Confirm
  | StoreTotalPrice
  | TestConfig
  | DisposeStorage
  | CreateThankU
  | CreateThankUBatch
  | ConfigToURL
  | ShareLink;

const cmdNone: Cmd = { cmdType: "None" };

async function executeCmd(state: State, cmd: Cmd): Promise<void> {
  console.log("executeCmd", cmd);
  switch (cmd.cmdType) {
    case "None": {
      // do nothing
      break;
    }

    case "Alert": {
      alert(cmd.message);
      break;
    }

    case "Confirm": {
      const confirmed = confirm(cmd.message);
      if (confirmed) sendMsg(cmd.onConfirm());
      break;
    }

    case "StoreTotalPrice": {
      try {
        localStorage.setItem("totalPrice", cmd.totalPrice.toString());
      } catch (err) {
        console.error(err);
      }
      break;
    }

    case "DisposeStorage": {
      try {
        localStorage.removeItem("totalPrice");
      } catch (err) {
        console.error(err);
      }
      break;
    }

    case "TestConfig": {
      try {
        await testConfig(cmd);
        sendMsg(cmd.onResult(null));
      } catch (err) {
        console.log(err);
        sendMsg(cmd.onResult(err as Error));
      }
      break;
    }

    case "CreateThankU": {
      const { donateeGroup, impactValue } = cmd;
      const { slug, token, message, orderId } = state;
      if (!slug || !token || !message || !orderId) {
        return alert("slug, token, message or orderId missing");
      }
      try {
        const url = await createThankU({
          simulate: false,
          slug,
          token,
          donateeGroup,
          impactValue,
          message,
          language: LANGUAGE,
          pid: `impact-generator-${new Date().getTime()}`,
          orderId,
        });
        sendMsg(cmd.onResult(url));
      } catch (err) {
        sendMsg(cmd.onResult(err as Error));
        console.error(err);
      }
      break;
    }

    case "CreateThankUBatch": {
      const { donateeGroup, impactValue, batchSize } = cmd;
      const { slug, token, message, orderId } = state;
      const startTime = new Date().getTime();
      if (!slug || !token || !message || !orderId) {
        return alert("slug, token, message or orderId missing");
      }
      await Promise.allSettled(
        [...Array(batchSize).keys()].map(async (batchNum) => {
          try {
            const url = await createThankU({
              simulate: false,
              slug,
              token,
              donateeGroup,
              impactValue,
              message,
              language: LANGUAGE,
              pid: `impact-generator-${startTime}-batch-${
                batchNum + 1
              }-of-${batchSize}`,
              orderId,
            });
            sendMsg(cmd.onEntryResult(url));
          } catch (err) {
            sendMsg(cmd.onEntryResult(err as Error));
            console.error(err);
          }
        })
      );
      sendMsg(cmd.onBatchDone());
      break;
    }

    case "ConfigToURL": {
      const {
        slug,
        token,
        orderId,
        message,
        budget,
        allow1sqm,
        hideFreeImpactSelection,
        buttonConfig,
      } = state;
      const url = new URL(location.toString());
      url.search = new URLSearchParams({
        slug: slug ?? "",
        token: token ?? "",
        orderId: orderId ?? "",
        message: message ?? "",
        budget: budget.toString(),
        ...(allow1sqm ? { allow1sqm: "true" } : {}),
        ...(hideFreeImpactSelection ? { hideFreeImpactSelection: "true" } : {}),
        buttonConfig: buttonConfigToString(buttonConfig, ","),
      }).toString();
      history.pushState({}, "", url);
      break;
    }

    case "ShareLink": {
      await navigator.share(
        { url: cmd.url, title: "", text: "" },
        /* extra options for https://www.npmjs.com/package/share-api-polyfill */
        { skype: false, language: "de" }
      );
      break;
    }
  }
}

// MESSAGE

type ConfigSubmitted = {
  msgType: "ConfigSubmitted";
  slug: string;
  token: string;
  orderId: string;
  message: string;
  budget: number;
  hideFreeImpactSelection: boolean;
  buttonConfig: Impact[];
};
type ConfigTested = {
  msgType: "ConfigTested";
  slug: string;
  token: string;
  orderId: string;
  result: null | Error;
  budget: number;
  hideFreeImpactSelection: boolean;
  buttonConfig: Impact[];
};
type ImpactSelected = {
  msgType: "ImpactSelected";
  donateeGroup: DonateeGroup;
  impactValue: number;
};
type GotThankU = {
  msgType: "GotThankU";
  donateeGroup: DonateeGroup;
  impactValue: number;
  url: URL;
};
type ThankUCreationFailed = {
  msgType: "ThankUCreationFailed";
  error: Error;
};
type GotBatchEntryResult = {
  msgType: "GotBatchEntryResult";
  result: URL | Error;
  donateeGroup: DonateeGroup;
  impactValue: number;
};
type BatchFinished = {
  msgType: "BatchFinished";
};
type NextButtonClicked = {
  msgType: "NextButtonClicked";
};
type SlugButtonClicked = {
  msgType: "SlugButtonClicked";
};
type ResetConfirmed = {
  msgType: "ResetConfirmed";
};
type ImpactIncremented = {
  msgType: "ImpactIncremented";
};
type ImpactDecremented = {
  msgType: "ImpactDecremented";
};
type BatchSizeChanged = {
  msgType: "BatchSizeChanged";
  batchSizeInputValue: string;
};
type ImpactTypeChanged = {
  msgType: "ImpactTypeChanged";
  impactType: DonateeGroup;
};
type ImpactValueChanged = {
  msgType: "ImpactValueChanged";
  impactInputValue: string;
};
type ImpactFormSubmitted = {
  msgType: "ImpactFormSubmitted";
};
type BackButtonClicked = {
  msgType: "BackButtonClicked";
};
type ShareButtonClicked = {
  msgType: "ShareButtonClicked";
};
type BatchBackButtonClicked = {
  msgType: "BatchBackButtonClicked";
};

type Msg =
  | ConfigSubmitted
  | ConfigTested
  | ImpactSelected
  | GotThankU
  | ThankUCreationFailed
  | GotBatchEntryResult
  | BatchFinished
  | NextButtonClicked
  | SlugButtonClicked
  | ResetConfirmed
  | ImpactDecremented
  | ImpactIncremented
  | BatchSizeChanged
  | ImpactTypeChanged
  | ImpactValueChanged
  | ImpactFormSubmitted
  | BackButtonClicked
  | ShareButtonClicked
  | BatchBackButtonClicked;

// INIT

function init(): [State, Cmd] {
  const isWof = location.hash === "#wof";
  const params = new URLSearchParams(location.search);

  const slug = params.get("slug");
  const token = params.get("token");
  const orderId = params.get("orderId");
  const message = params.get("message");

  const allow1sqm = isWof || params.get("allow1sqm") === "true";
  const hideFreeImpactSelection =
    isWof || params.get("hideFreeImpactSelection") === "true";

  const buttonConfig = (() => {
    const buttonConfigFromParams = params.get("buttonConfig");
    // Wheel Of Fortune preset
    if (isWof) {
      return [
        { type: "ProtectWildlife", value: 1 },
        { type: "ProtectWildlife", value: 2 },
        { type: "ProtectWildlife", value: 3 },
        { type: "PlantTrees", value: 1 },
        { type: "CleanOcean", value: 1 },
      ] as Impact[];
    }
    // parse from URL params
    if (buttonConfigFromParams) {
      const config = parseButtonConfig(buttonConfigFromParams, ",");
      if (config.length > 0) {
        return config;
      }
    }
    // default
    return [
      { type: "PlantTrees", value: 1 },
      { type: "ProtectWildlife", value: 10 },
      { type: "CleanOcean", value: 1 },
    ] as Impact[];
  })();

  const budget = Math.max(parseInt(params.get("budget") || "0"), MIN_BUDGET);

  let totalPrice: number = 0;
  try {
    totalPrice = parseFloat(localStorage.getItem("totalPrice") || "0");
  } catch (error) {
    console.error(error);
  }

  return [
    {
      ...initialState,
      slug,
      token,
      orderId,
      message,
      budget,
      totalPrice,
      allow1sqm,
      hideFreeImpactSelection,
      buttonConfig,
      page: "init",
    },
    cmdNone,
  ];
}

// UPDATE

function update(state: State, msg: Msg): [State, Cmd] {
  switch (msg.msgType) {
    case "ConfigSubmitted": {
      const {
        slug,
        token,
        orderId,
        message,
        budget,
        hideFreeImpactSelection,
        buttonConfig,
      } = msg;
      return [
        { ...state, message, page: "loading" },
        {
          cmdType: "TestConfig",
          slug,
          token,
          orderId,
          message,
          onResult: (result) => ({
            msgType: "ConfigTested",
            slug,
            token,
            orderId,
            result,
            budget,
            hideFreeImpactSelection,
            buttonConfig,
          }),
        },
      ];
    }

    case "ConfigTested": {
      const {
        result,
        slug,
        token,
        orderId,
        budget,
        hideFreeImpactSelection,
        buttonConfig,
      } = msg;
      return result === null
        ? [
            {
              ...state,
              slug,
              token,
              orderId,
              budget,
              hideFreeImpactSelection,
              buttonConfig,
              page: "select",
            },
            { cmdType: "ConfigToURL" },
          ]
        : [
            { ...state, page: "init" },
            {
              cmdType: "Alert",
              message: `Konfiguration fehlgeschlagen (${result.message})`,
            },
          ];
    }

    case "ImpactSelected": {
      const { donateeGroup, impactValue } = msg;
      return stepCreateThankU({
        state,
        donateeGroup,
        impactValue,
        batchSize: 1,
      });
    }

    case "ImpactDecremented": {
      const diff = getImpactValueStep(state);
      const impactValue = toImpactValue(state) - diff;
      return [
        impactValue < diff
          ? state
          : { ...state, impactInputValue: impactValue.toString() },
        cmdNone,
      ];
    }

    case "ImpactIncremented": {
      const diff = getImpactValueStep(state);
      const impactValue = toImpactValue(state) + diff;
      return [{ ...state, impactInputValue: impactValue.toString() }, cmdNone];
    }

    case "BatchSizeChanged": {
      return [
        { ...state, batchSizeInputValue: msg.batchSizeInputValue },
        cmdNone,
      ];
    }

    case "ImpactTypeChanged": {
      const { impactType: prevImpactType } = state;
      if (prevImpactType === msg.impactType) {
        return [state, cmdNone];
      }
      const nextImpactValue = (() => {
        const impactValue = toImpactValue(state);
        if (prevImpactType === "ProtectWildlife") return impactValue / 10;
        if (msg.impactType === "ProtectWildlife") return impactValue * 10;
        return impactValue;
      })();
      return [
        {
          ...state,
          impactInputValue: nextImpactValue.toString(),
          impactType: msg.impactType,
        },
        cmdNone,
      ];
    }

    case "ImpactValueChanged": {
      return [{ ...state, impactInputValue: msg.impactInputValue }, cmdNone];
    }

    case "ImpactFormSubmitted": {
      const donateeGroup = state.impactType;
      const impactValue = toImpactValue(state);
      const batchSize = toBatchSize(state);
      return stepCreateThankU({ state, donateeGroup, impactValue, batchSize });
    }

    case "BackButtonClicked": {
      return [{ ...state, page: "init" }, cmdNone];
    }

    case "BatchBackButtonClicked": {
      if (state.batchStatus === "processing") {
        return [
          state,
          {
            cmdType: "Alert",
            message: "Bitte warten! Batch-Prozess ist in Bearbeitung.",
          },
        ];
      } else {
        return [{ ...state, page: "init" }, cmdNone];
      }
    }

    case "GotThankU": {
      const totalPrice =
        state.totalPrice + PRICING[msg.donateeGroup] * msg.impactValue;
      const thanku = {
        url: msg.url,
        donateeGroup: msg.donateeGroup,
        impactValue: msg.impactValue,
      };
      return [
        { ...state, page: "qrcode", thanku, totalPrice },
        { cmdType: "StoreTotalPrice", totalPrice },
      ];
    }

    case "ThankUCreationFailed": {
      return [
        { ...state, page: "select" },
        {
          cmdType: "Alert",
          message: `ThankU konnte nicht erstellt werden (${msg.error.message})`,
        },
      ];
    }

    case "GotBatchEntryResult": {
      if (msg.result instanceof URL) {
        const url = msg.result;
        const entry: BatchEntry = { status: "Ok", link: url.toString() };
        const batchResult = [entry, ...state.batchResult];
        return [{ ...state, batchResult }, cmdNone];
      } else {
        const error = msg.result;
        const entry: BatchEntry = { status: "Err", error: error.message };
        const totalPrice =
          state.totalPrice + PRICING[msg.donateeGroup] * msg.impactValue;
        const batchResult = [entry, ...state.batchResult];
        return [{ ...state, batchResult, totalPrice }, cmdNone];
      }
    }

    case "BatchFinished": {
      return [{ ...state, batchStatus: "done" }, cmdNone];
    }

    case "NextButtonClicked": {
      return [{ ...state, page: "select" }, cmdNone];
    }

    case "SlugButtonClicked": {
      return [
        state,
        {
          cmdType: "Confirm",
          message: "Zurücksetzen?",
          onConfirm: () => ({ msgType: "ResetConfirmed" }),
        },
      ];
    }

    case "ResetConfirmed": {
      return [
        { ...state, totalPrice: 0, budget: MIN_BUDGET, page: "init" },
        { cmdType: "DisposeStorage" },
      ];
    }

    case "ShareButtonClicked": {
      const url = state.thanku?.url.toString();
      if (!url) {
        console.error("URL of ThankU missing");
        return [state, cmdNone];
      }
      return [state, { cmdType: "ShareLink", url }];
    }
  }
}

function toImpactValue(state: State): number {
  const min = getImpactValueStep(state);
  const value = parseInt(state.impactInputValue) || min;
  return Math.round(value / min) * min;
}

function toBatchSize(state: State): number {
  return Math.max(1, parseInt(state.batchSizeInputValue) || 1);
}

function getImpactValueStep(state: State): number {
  switch (state.impactType) {
    case "ProtectWildlife":
      return state.allow1sqm ? 1 : 10;
    case "PlantTrees":
      return 1;
    case "CleanOcean":
      return 1;
  }
}

function stepCreateThankU({
  state,
  donateeGroup,
  impactValue,
  batchSize,
}: {
  state: State;
  donateeGroup: DonateeGroup;
  impactValue: number;
  batchSize: number;
}): [State, Cmd] {
  const price = PRICING[donateeGroup] * impactValue * batchSize;
  if (toBudgetLeft(state) < price) {
    return [
      state,
      {
        cmdType: "Alert",
        message: "Das Budget reicht für diese Aktion nicht aus",
      },
    ];
  }
  if (batchSize > 1) {
    return [
      { ...state, page: "batch", batchResult: [], batchStatus: "processing" },
      {
        cmdType: "CreateThankUBatch",
        donateeGroup,
        impactValue,
        batchSize,
        onEntryResult: (urlOrErr) => ({
          msgType: "GotBatchEntryResult",
          result: urlOrErr,
          donateeGroup,
          impactValue,
        }),
        onBatchDone: () => ({ msgType: "BatchFinished" }),
      },
    ];
  }
  return [
    { ...state, page: "loading" },
    {
      cmdType: "CreateThankU",
      donateeGroup,
      impactValue,
      onResult: (urlOrErr) =>
        urlOrErr instanceof URL
          ? {
              msgType: "GotThankU",
              url: urlOrErr,
              donateeGroup,
              impactValue,
            }
          : { msgType: "ThankUCreationFailed", error: urlOrErr },
    },
  ];
}

// VIEW

function setupView(state: State): void {
  // init
  dom.init.form.addEventListener("submit", (event) => {
    event.preventDefault();
    sendMsg({
      msgType: "ConfigSubmitted",
      slug: dom.init.inputSlug.value.trim(),
      token: dom.init.inputToken.value.trim(),
      orderId: dom.init.inputOrderId.value.trim(),
      message: dom.init.inputMessage.value.trim(),
      budget: parseInt(dom.init.inputBudget.value),
      hideFreeImpactSelection: !dom.init.checkboxFreeImpactSelection.checked,
      buttonConfig: parseButtonConfig(dom.init.buttonConfig.value, "\n"),
    });
  });

  // select
  dom.select.decImpactValue.addEventListener("click", () => {
    sendMsg({ msgType: "ImpactDecremented" });
  });
  dom.select.incImpactValue.addEventListener("click", () => {
    sendMsg({ msgType: "ImpactIncremented" });
  });
  dom.select.impactType.addEventListener("change", () => {
    sendMsg({
      msgType: "ImpactTypeChanged",
      impactType: dom.select.impactType.value as DonateeGroup,
    });
  });
  dom.select.impactValue.addEventListener("input", () => {
    sendMsg({
      msgType: "ImpactValueChanged",
      impactInputValue: dom.select.impactValue.value,
    });
  });
  dom.select.impactValue.addEventListener("blur", () => {
    sendMsg({
      msgType: "ImpactValueChanged",
      impactInputValue: toImpactValue(getState()).toString(),
    });
  });
  dom.select.decBatchSize.addEventListener("click", () => {
    sendMsg({
      msgType: "BatchSizeChanged",
      batchSizeInputValue: Math.max(1, toBatchSize(getState()) - 1).toString(),
    });
  });
  dom.select.incBatchSize.addEventListener("click", () => {
    sendMsg({
      msgType: "BatchSizeChanged",
      batchSizeInputValue: (toBatchSize(getState()) + 1).toString(),
    });
  });
  dom.select.batchSize.addEventListener("input", () => {
    sendMsg({
      msgType: "BatchSizeChanged",
      batchSizeInputValue: dom.select.batchSize.value,
    });
  });
  dom.select.batchSize.addEventListener("blur", () => {
    sendMsg({
      msgType: "BatchSizeChanged",
      batchSizeInputValue: toBatchSize(getState()).toString(),
    });
  });
  dom.select.impactForm.addEventListener("submit", (event) => {
    event.preventDefault();
    sendMsg({
      msgType: "ImpactFormSubmitted",
    });
  });
  dom.select.btnBack.addEventListener("click", () => {
    sendMsg({ msgType: "BackButtonClicked" });
  });
  updateSelectedImpact(state);

  // qrcode
  dom.qrcode.btnNext.addEventListener("click", () => {
    sendMsg({ msgType: "NextButtonClicked" });
  });
  dom.qrcode.btnShare.addEventListener("click", () => {
    sendMsg({ msgType: "ShareButtonClicked" });
  });

  // batch
  dom.batch.btnBack.addEventListener("click", () => {
    sendMsg({ msgType: "BatchBackButtonClicked" });
  });

  // footer
  dom.slug.addEventListener("click", () => {
    sendMsg({ msgType: "SlugButtonClicked" });
  });
}

function updateSelectedImpact(state: State): void {
  dom.select.impactType.value = state.impactType;
  dom.select.impactValue.value = state.impactInputValue;
  dom.select.batchSize.value = state.batchSizeInputValue;
  dom.select.costsImpact.textContent = formatPrice(
    PRICING[state.impactType] * toImpactValue(state) * toBatchSize(state)
  );
}

function updateBatch(state: State): void {
  dom.batch.countCurrent.textContent = state.batchResult.length.toString();
  dom.batch.countErr.textContent = state.batchResult
    .filter((entry) => entry.status === "Err")
    .length.toString();
  dom.batch.countOk.textContent = state.batchResult
    .filter((entry) => entry.status === "Ok")
    .length.toString();
  dom.batch.countTotal.textContent = toBatchSize(state).toString();
  dom.batch.linkList.value = state.batchResult
    .map((entry) => {
      switch (entry.status) {
        case "Ok":
          return `[Ok] ${entry.link}`;
        case "Err":
          return `[Err] ${entry.error}`;
      }
    })
    .join("\n");

  const batchLinkList = state.batchResult.reduce<string[]>((acc, entry) => {
    if (entry.status === "Ok") {
      return [...acc, entry.link];
    }
    return acc;
  }, []);
  dom.batch.loading.toggleAttribute(
    "hidden",
    state.batchStatus !== "processing"
  );
  dom.batch.downloadLink.classList.toggle(
    "disabled",
    batchLinkList.length === 0 || state.batchStatus !== "done"
  );
  dom.batch.downloadLink.href =
    batchLinkList.length === 0 || state.batchStatus !== "done"
      ? ""
      : URL.createObjectURL(
          new Blob([["ThankU-Links", ...batchLinkList].join("\n")], {
            type: "text/csv",
          })
        );
}

function updateButtonConfig(state: State): void {
  dom.init.buttonConfig.value = buttonConfigToString(state.buttonConfig, "\n");
  dom.select.impactQuickSelection
    .querySelectorAll("button")
    .forEach((button) => {
      button.remove();
    });
  state.buttonConfig.forEach((impact) => {
    const btn = (
      dom.select.impactButtonTemplate.content.cloneNode(
        true
      ) as DocumentFragment
    ).querySelector("button") as HTMLButtonElement;
    btn.querySelector("strong")!.textContent = humanizeImpact(impact);
    btn.querySelector("span")!.textContent = formatPrice(
      PRICING[impact.type] * impact.value
    );
    btn.addEventListener("click", () => {
      sendMsg({
        msgType: "ImpactSelected",
        donateeGroup: impact.type,
        impactValue: impact.value,
      });
    });
    dom.select.impactQuickSelection.appendChild(btn);
  });
}

function updateView(prevState: State | null, state: State): void {
  const hasChanged = (key: StateKey): boolean =>
    prevState?.[key] !== state[key];

  if (hasChanged("isDev")) {
    dom.dev.toggleAttribute("hidden", !state.isDev);
  }
  if (hasChanged("page")) {
    showPage(state.page);
  }
  if (hasChanged("slug")) {
    dom.init.inputSlug.value = state.slug ?? "";
    if (state.slug) {
      dom.slug.innerText = state.slug;
      dom.slug.toggleAttribute("hidden", false);
    } else {
      dom.slug.toggleAttribute("hidden", true);
    }
  }
  if (hasChanged("token")) {
    dom.init.inputToken.value = state.token ?? "";
  }
  if (hasChanged("orderId")) {
    dom.init.inputOrderId.value = state.orderId ?? "";
  }
  if (hasChanged("message")) {
    dom.init.inputMessage.value = state.message ?? "";
  }
  if (hasChanged("totalPrice")) {
    dom.counter.textContent = formatPrice(toBudgetLeft(state));
  }
  if (hasChanged("budget")) {
    dom.init.inputBudget.value = state.budget.toString();
    dom.counter.textContent = formatPrice(toBudgetLeft(state));
  }
  if (hasChanged("thanku") && state.thanku) {
    updateQRCode(state.thanku.url.toString());
    dom.qrcode.impact.innerText = humanizeImpact({
      type: state.thanku.donateeGroup,
      value: state.thanku.impactValue,
    });
  }
  if (
    hasChanged("impactType") ||
    hasChanged("impactInputValue") ||
    hasChanged("batchSizeInputValue")
  ) {
    updateSelectedImpact(state);
  }
  if (hasChanged("hideFreeImpactSelection")) {
    dom.init.checkboxFreeImpactSelection.checked =
      !state.hideFreeImpactSelection;
    dom.select.impactForm.toggleAttribute(
      "hidden",
      state.hideFreeImpactSelection
    );
  }
  if (hasChanged("buttonConfig")) {
    updateButtonConfig(state);
  }
  if (
    hasChanged("batchStatus") ||
    hasChanged("batchResult") ||
    hasChanged("batchSizeInputValue")
  ) {
    updateBatch(state);
  }
}

// HELPERS

function parseButtonConfig(str: string, separator: string): Impact[] {
  return str
    .replace(/ /g, "")
    .split(separator)
    .reduce<Impact[]>((result, elem) => {
      const [typeString, valueString] = elem.split(":");
      const value = parseInt(valueString);
      const type = isDonateeGroup(typeString) ? typeString : null;
      return type && value > 0 ? [...result, { type, value }] : result;
    }, []);
}

function buttonConfigToString(config: Impact[], separator: string): string {
  return config.map(({ type, value }) => `${type}:${value}`).join(separator);
}

function isDonateeGroup(str: string): str is DonateeGroup {
  return !!donateeGroups.find((donateeGroup) => donateeGroup === str);
}

function toBudgetLeft(state: State): number {
  return Math.max(0, state.budget - state.totalPrice);
}

function formatPrice(value: number): string {
  return value.toFixed(2).replace(".", ",") + "€";
}

function humanizeImpact(impact: Impact): string {
  switch (impact.type) {
    case "PlantTrees":
      return impact.value === 1
        ? "1 Baum pflanzen"
        : `${impact.value} Bäume pflanzen`;
    case "CleanOcean":
      return `${impact.value} kg Ozeanplastik entfernen`;
    case "ProtectWildlife":
      return `${impact.value} m² Habitat schützen`;
  }
}

declare global {
  interface Window {
    __state__: State | undefined;
  }
}

function getState(): State {
  return window["__state__"] || initialState;
}

function setState(state: State): void {
  if (window["__state__"] === state) return;
  window["__state__"] = state;
  console.log("State updated: ", state);
}

function run(): void {
  let [state, cmd] = init();
  updateView(null, state);
  setupView(state);
  setState(state);
  executeCmd(state, cmd);
}

function sendMsg(msg: Msg) {
  console.log("sendMsg", msg);
  const state = getState();
  const [newState, cmd] = update(state, msg);
  updateView(state, newState);
  setState(newState);
  executeCmd(newState, cmd);
}

function $(selector: string): Element {
  const el = document.querySelector(selector);
  if (!el) throw Error(`Element ${selector} not found`);
  return el;
}

function updateQRCode(url: string): void {
  QRCode.toCanvas(
    dom.qrcode.canvas,
    url,
    {
      scale: 6,
      margin: 2,
      color: {
        dark: "#10162A",
      },
    },
    function (error) {
      if (error) {
        dom.qrcode.url.textContent = `Fehler: ${error}`;
        dom.qrcode.url.setAttribute("href", "#");
      } else {
        dom.qrcode.url.textContent = url;
        dom.qrcode.url.setAttribute("href", url);
      }
    }
  );
}

function showPage(id: PageId | null): void {
  pageIds.forEach((pageId) => {
    const el = dom.page[pageId];
    if (pageId === id) {
      el.toggleAttribute("hidden", false);
    } else {
      el.toggleAttribute("hidden", true);
    }
  });
}

async function testConfig({
  slug,
  token,
  orderId,
  message,
}: {
  slug: string;
  token: string;
  orderId: string;
  message: string;
}): Promise<void> {
  await createThankU({
    simulate: true, // !!!
    slug,
    token,
    donateeGroup: "PlantTrees",
    impactValue: 1,
    message,
    language: LANGUAGE,
    pid: "impact-generator-testConfig",
    orderId,
  });
}

async function createThankU(o: {
  simulate: boolean;
  slug: string;
  donateeGroup: DonateeGroup;
  impactValue: number;
  message: string;
  language: Language;
  pid: string;
  token: string;
  orderId: string;
}): Promise<URL> {
  const {
    simulate,
    slug,
    donateeGroup,
    impactValue,
    message,
    language,
    pid,
    token,
    orderId,
  } = o;
  const msg = [slug, donateeGroup, impactValue, message, language, pid].join(
    "+"
  );
  const sig = hmacSHA256(msg, token).toString();
  const response = await fetch(
    ["/api/donate/postpaid", simulate ? "?simulate=true" : ""].join(""),
    {
      method: "POST",
      headers: { "x-sig": sig, "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        donateeGroup,
        impactValue,
        message,
        language,
        pid,
        orderId,
      }),
    }
  );
  if (response.status === 400) {
    throw Error("Bad request");
  }
  if (response.status < 200 || response.status > 201) {
    throw Error(`Failed with ${response.status}`);
  }
  const { id } = await response.json();
  return toThankULink({ id, language, slug });
}

function toThankULink(o: {
  id: string;
  language: Language;
  slug: string;
}): URL {
  return new URL(
    [
      BASE_URL,
      o.language === "de" ? "danke/von" : "thanks/from",
      o.slug,
      o.id,
    ].join("/")
  );
}
